<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="sizeClass"
      :style="{ '--pin-active-color': 'var(--device-color-switch)' }"
      :title="device.deviceName">
      <!-- The drawn person inside the status ring (P14): present fills the
           family's green ring, an absent client keeps the track. -->
      <ControlStatusRing :value="present ? 100 : 0">
        <ControlIcon
          name="person"
          :size="pinIconSize"
          :style="present ? { color: 'var(--device-color-switch)' } : {}" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'smartphone'"
      :name="displayName"
      :is-on="present"
      active-color="green-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control / full mode ───────────────────────────────────────────── -->
  <!-- No head of its own: the sheet's chrome paints the circle, the name and
       the presence state — the body starts at the readings. -->
  <template v-else>
    <div class="client-sheet">
      <div class="client-grid">
        <!-- The class's own state in the sheet language: present rides the
             switch token, away falls to the muted ink — no hard-coded
             utilities. -->
        <div :style="present ? sheetAccentStyle('var(--device-color-switch)') : undefined">
          <ControlValueTile
            icon="person"
            :label="tt('Presence', 'Prisotnost')"
            :value="present ? tt('Present', 'Prisoten') : absent ? tt('Away', 'Odsoten') : tt('Unknown', 'Neznano')"
            :tone="present ? 'accent' : 'muted'" />
        </div>
        <ControlValueTile
          v-if="hostnameValue"
          icon="computer"
          :label="tt('Hostname', 'Ime gostitelja')"
          :value="hostnameValue" />
        <ControlValueTile v-if="ipValue" icon="pin" :label="tt('IP', 'IP')" :value="ipValue" />
        <ControlValueTile v-if="essidValue" icon="wind" :label="tt('Network', 'Omrežje')" :value="essidValue" />
        <ControlValueTile v-if="macValue" icon="key" :label="tt('MAC', 'MAC')" :value="macValue" />
        <ControlValueTile
          icon="bolt"
          :label="tt('Connection', 'Povezava')"
          :value="wiredValue ? tt('Wired', 'Žično') : 'WiFi'" />
        <ControlValueTile
          v-if="signalValue !== null"
          icon="activity"
          :label="tt('Signal', 'Signal')"
          :value="String(signalValue)"
          unit="dBm" />
        <ControlValueTile icon="clock" :label="tt('Last seen', 'Nazadnje videno')" :value="lastSeenAgo || '—'" />
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import { sheetAccentStyle } from '../device-sheet/sheetAccent';
import { useDeviceValues } from '../../composables/useDeviceValues';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

const { currentValues, initializeValues } = useDeviceValues(props.device.id);
if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

function valueDef(key: string): DeviceValueWithReferences | undefined {
  return (props.device.deviceValues ?? []).find((v) => v.valueKey === key);
}

function readValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return valueDef(key)?.currentValue ?? undefined;
}

function boolValue(key: string): boolean {
  const v = readValue(key);
  return v === true || v === 1 || v === 'true' || v === 'on';
}

const present = computed<boolean>(() => boolValue('present'));
const absent = computed<boolean>(() => {
  const v = readValue('present');
  return v === false || v === 0 || v === 'false' || v === 'off';
});

const hostnameValue = computed<string | null>(() => {
  const v = readValue('hostname');
  return v ? String(v) : null;
});
const ipValue = computed<string | null>(() => {
  const v = readValue('ip');
  return v ? String(v) : null;
});
const macValue = computed<string | null>(() => {
  const v = readValue('mac');
  return v ? String(v) : null;
});
const wiredValue = computed<boolean>(() => boolValue('wired'));
const essidValue = computed<string | null>(() => {
  const v = readValue('essid');
  return v ? String(v) : null;
});
const signalValue = computed<number | null>(() => {
  const v = readValue('signal');
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isFinite(n) ? n : null;
});
const lastSeenValue = computed<number | null>(() => {
  const v = readValue('last_seen');
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isFinite(n) && n > 0 ? n : null;
});

const lastSeenAgo = computed<string | null>(() => {
  const seconds = lastSeenValue.value;
  if (seconds === null) return null;
  const ageSec = Math.max(0, Math.floor(Date.now() / 1000) - seconds);
  if (ageSec < 60) return tt('just now', 'pravkar');
  const minutes = Math.floor(ageSec / 60);
  if (minutes < 60) return tt(`${minutes}m ago`, `${minutes} min nazaj`);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tt(`${hours}h ago`, `${hours} h nazaj`);
  const days = Math.floor(hours / 24);
  return tt(`${days}d ago`, `${days} d nazaj`);
});

const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn person's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});
</script>

<style scoped>
/* Control mode: the readings in the sheet language. The host chrome owns the
   head and the panel; this block is the tile grid. */
.client-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

.client-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

@media (min-width: 640px) {
  .client-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
